import { CancelablePromise, makeCancelable } from '@upsales/components/Utils/CancelablePromise';
import { useSelector } from 'App/components/hooks';
import { Loader, Flex, Row, Text, Link } from '@upsales/components';
import SimilarCustomersResource from 'Resources/SimilarCustomers';
import React, { useState, useEffect, useRef } from 'react';
import Bem from '@upsales/components/Utils/bemClass';
import Client from 'App/resources/Model/Client';
import T from 'Components/Helpers/translate';
import logError from 'Helpers/logError';
import { RootState } from 'Store/index';
import './SimilarCustomers.scss';
import StateRouter from 'App/services/StateRouter';

type Props = {
	client: Client;
	bold?: boolean;
	renderTitle?: boolean;
	variant?: 'clientCard' | 'editModal' | 'editDrawer';
	close?: () => void;
};

type SimilarCustomersResponse = {
	data: SimilarCustomer[];
};

type SimilarCustomer = {
	id: number;
	name: string;
	code: string;
	sales: number;
	takeRate: number | null;
};

const SimilarCustomers = ({ client, bold = true, renderTitle = false, variant = 'clientCard', close }: Props) => {
	const classes = new Bem('SimilarCustomers');
	const lang = {
		similarCustomers: T('default.similarCustomers'),
		and: T('default.and')
	};
	const getSimilarCustomersPromise = useRef<CancelablePromise<
		Awaited<ReturnType<typeof SimilarCustomersResource.get>>
	> | null>(null);

	const { customerId } = useSelector(({ App }: RootState) => App);
	const [similarCustomers, setSimilarCustomers] = useState<SimilarCustomersResponse | null>(null);
	const [loading, setLoading] = useState(false);

	const goToAccount = (id: number) => {
		StateRouter.go(
			'account.dashboard',
			{
				customerId,
				id
			},
			{
				inherit: false
			}
		);
	};

	const getSpacing = () => {
		switch (variant) {
			case 'editDrawer':
			case 'editModal':
				return 'mts mbs';
			case 'clientCard':
			default:
				return 'mtm mbs';
		}
	};

	useEffect(() => {
		if (!client.id) return;

		setLoading(true);
		getSimilarCustomersPromise.current = makeCancelable(SimilarCustomersResource.get(client.id));

		getSimilarCustomersPromise.current.promise
			.then((data: SimilarCustomersResponse) => {
				setSimilarCustomers(data);
			})
			.catch((err: Error) => {
				if (!err.message.includes('canceled')) {
					logError(err, 'Failed to fetch similar customers');
				}
				setSimilarCustomers(null);
			})
			.finally(() => setLoading(false));

		return () => {
			getSimilarCustomersPromise.current?.cancel();
		};
	}, [client.id]);

	if (loading) return <Loader size="sm" />;

	return (
		<>
			{similarCustomers?.data && similarCustomers.data.length > 0 ? (
				<Flex direction="column" space={getSpacing()}>
					{renderTitle ? (
						<Text size="sm" bold={variant === 'clientCard' && bold}>
							{lang.similarCustomers}
						</Text>
					) : null}
					<Flex className={classes.mod(variant).b()} direction="column" gap="u2" flex={1}>
						<Row>
							{similarCustomers?.data
								?.map((item: SimilarCustomer) => (
									<Link
										className={classes.elem('customer').b()}
										key={item.id}
										onClick={e => {
											e.stopPropagation();
											close?.(); // Closes e.g. EditPhoneCall or EditOrder
											goToAccount(item.id);
										}}
										target="_blank"
										nostyle
									>
										{item.name}
									</Link>
								))
								.reduce((prev: React.ReactNode[], curr: React.ReactNode, i: number) => {
									if (i === 0) return [curr];
									return [
										...prev,
										<Text key={`separator-${i}`} className={classes.elem('and').b()} size="sm">
											{lang.and.toLowerCase()}
										</Text>,
										curr
									];
								}, [])}
						</Row>
					</Flex>
				</Flex>
			) : null}
		</>
	);
};

export default SimilarCustomers;
